import React from 'react';
import { X } from 'lucide-react';

const ReminderViewModal = ({ show, onClose, reminder }) => {
  if (!show || !reminder) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getFrequencyText = (frequency, recurrencePattern) => {
    if (frequency !== 'none') return frequency;
    if (recurrencePattern?.type === 'monthly') {
      return `Monthly on day ${recurrencePattern.dayOfMonth}`;
    }
    if (recurrencePattern?.type === 'weekly') {
      return 'Weekly';
    }
    if (recurrencePattern?.type === 'yearly') {
      return 'Yearly';
    }
    return 'One-time';
  };

  const getDueInfo = () => {
    if (!reminder.date) return { text: '-', color: '#707EAE' };
    
    const dueDate = new Date(reminder.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    const diffTime = dueDate - today;
    const daysUntil = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (reminder.isCompleted) {
      return { text: 'Completed', color: '#10b981' };
    }
    if (daysUntil < 0) {
      const daysOverdue = Math.abs(daysUntil);
      return { text: `Overdue by ${daysOverdue} day${daysOverdue !== 1 ? 's' : ''}`, color: '#ef4444' };
    }
    if (daysUntil === 0) {
      return { text: 'Due Today', color: '#f59e0b' };
    }
    if (daysUntil === 1) {
      return { text: 'Due Tomorrow', color: '#f59e0b' };
    }
    if (daysUntil <= 7) {
      return { text: `Due in ${daysUntil} days`, color: '#eab308' };
    }
    return { text: `Due in ${daysUntil} days`, color: '#3b82f6' };
  };

  const dueInfo = getDueInfo();

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-collaborator-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '650px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="share-modal-close-btn" onClick={onClose}>
          <X size={24} />
        </button>

        <div className="share-modal-header" style={{ marginBottom: '20px' }}>
          <h2 className="share-modal-title">Reminder Details</h2>
          <span className="badge" style={{
            background: reminder.isCompleted ? '#6b7280' : '#10b981',
            color: '#fff',
            fontSize: '11px',
            padding: '4px 12px',
            borderRadius: '12px'
          }}>
            {reminder.isCompleted ? 'Inactive' : 'Active'}
          </span>
        </div>

        {/* Main Description Card */}
        <div style={{ 
          background: '#1D19E4', 
          padding: '20px', 
          borderRadius: '12px', 
          marginBottom: '16px',
          color: 'white'
        }}>
          <div style={{ fontSize: '12px', opacity: 0.9, marginBottom: '6px', letterSpacing: '0.5px' }}>DESCRIPTION</div>
          <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>
            {reminder.description || '-'}
          </div>
          <div style={{ 
            display: 'inline-block',
            background: 'rgba(255,255,255,0.2)', 
            padding: '6px 12px', 
            borderRadius: '20px',
            fontSize: '13px',
            fontWeight: '600'
          }}>
            {dueInfo.text}
          </div>
        </div>

        {/* Related Item Card */}
        {(reminder.relatedItemName || reminder.asset?.name || reminder.liability?.name || 
          reminder.asset?._id || reminder.liability?._id) && (
          <div style={{ 
            border: '1px solid #E8E8FC', 
            padding: '16px', 
            borderRadius: '12px', 
            marginBottom: '16px',
            background: '#fff'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
              <div>
                <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>RELATED ITEM</div>
                <div style={{ fontSize: '16px', fontWeight: '700', color: '#2b3674' }}>
                  {reminder.relatedItemName || reminder.asset?.name || reminder.liability?.name || 'Asset/Liability'}
                </div>
              </div>
              <span className="badge" style={{ 
                background: reminder.referenceType === 'asset' ? '#E8E8FC' : '#fff3e0',
                color: reminder.referenceType === 'asset' ? '#1D19E4' : '#f57c00',
                fontSize: '11px',
                padding: '4px 10px',
                borderRadius: '12px',
                textTransform: 'capitalize',
                fontWeight: '600'
              }}>
                {reminder.referenceType || '-'}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              {(reminder.relatedItemType || reminder.asset?.assetType || reminder.liability?.liabilityType) && (
                <div>
                  <div style={{ fontSize: '11px', color: '#707EAE', marginBottom: '2px' }}>Type</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', textTransform: 'capitalize', color: '#2b3674' }}>
                    {reminder.relatedItemType || reminder.asset?.assetType || reminder.liability?.liabilityType}
                  </div>
                </div>
              )}
              {(reminder.asset?.status || reminder.liability?.status) && (
                <div>
                  <div style={{ fontSize: '11px', color: '#707EAE', marginBottom: '2px' }}>Status</div>
                  <span className="badge" style={{
                    background: (reminder.asset?.status || reminder.liability?.status) === 'active' ? '#d5f3e9' : '#fee',
                    color: (reminder.asset?.status || reminder.liability?.status) === 'active' ? '#0c9f6e' : '#ef4444',
                    fontSize: '11px',
                    padding: '3px 8px',
                    borderRadius: '12px',
                    textTransform: 'capitalize',
                    fontWeight: '600'
                  }}>
                    {reminder.asset?.status || reminder.liability?.status}
                  </span>
                </div>
              )}
              {(reminder.asset?.estimatedValue || reminder.liability?.outstandingAmount) && (
                <div>
                  <div style={{ fontSize: '11px', color: '#707EAE', marginBottom: '2px' }}>Value</div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#2b3674' }}>
                    ${(reminder.asset?.estimatedValue || reminder.liability?.outstandingAmount).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Info Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px', marginBottom: '16px' }}>
          {/* Reminder Type */}
          <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>REMINDER TYPE</div>
            <div style={{ fontSize: '14px', fontWeight: '600', textTransform: 'capitalize', color: '#2b3674' }}>{reminder.type || '-'}</div>
          </div>

          {/* Frequency */}
          <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>FREQUENCY</div>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#2b3674' }}>{getFrequencyText(reminder.frequency, reminder.recurrencePattern)}</div>
          </div>

          {/* Due Date */}
          <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>DUE DATE</div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#2b3674' }}>{formatDate(reminder.date)}</div>
          </div>

          {/* Created */}
          <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>CREATED</div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#2b3674' }}>{formatDate(reminder.createdAt)}</div>
          </div>

          {/* Last Run - Only if exists */}
          {reminder.recurrencePattern?.lastRun && (
            <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>LAST RUN</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#2b3674' }}>{formatDate(reminder.recurrencePattern.lastRun)}</div>
            </div>
          )}

          {/* Completed At - Only if exists */}
          {reminder.completedAt && (
            <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
              <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>COMPLETED AT</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: '#2b3674' }}>{formatDate(reminder.completedAt)}</div>
            </div>
          )}

          {/* Last Updated */}
          <div style={{ padding: '12px', background: '#F4F7FE', borderRadius: '8px' }}>
            <div style={{ fontSize: '11px', color: '#707EAE', fontWeight: '600', marginBottom: '4px', letterSpacing: '0.5px' }}>LAST UPDATED</div>
            <div style={{ fontSize: '13px', fontWeight: '600', color: '#2b3674' }}>{formatDate(reminder.updatedAt)}</div>
          </div>
        </div>

        <div className="share-form-actions" style={{ marginTop: '20px' }}>
          <button type="button" className="share-btn-cancel" onClick={onClose} style={{ width: '100%' }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReminderViewModal;
